import type { ILanguage, SQDCEventActionTypeFieldType } from "@/graphql";
import { languageNames } from "@/language";

export type IshikawaAreaConfig = {
  id: string;
  nameId?: number;
  translations?: Partial<ILanguage>;
};

export type IshikawaProblemSource = {
  rowIndex: number;
  columnIndex: number;
};

export type IshikawaFieldConfig = {
  areas: IshikawaAreaConfig[];
  problemSource: IshikawaProblemSource | null;
};

export const MAX_ISHIKAWA_AREAS = 10;

const DEFAULT_AREA_DEFINITIONS = [
  [
    "HUMAN",
    {
      de: "Mensch",
      de_CH: "Mensch",
      en: "People",
      en_GB: "People",
      es: "Personas",
      fr: "Personnes",
      hu: "Emberek",
      it: "Persone",
      pl: "Ludzie",
      pt: "Pessoas",
      uk_UA: "Люди",
    },
  ],
  [
    "MACHINE",
    {
      de: "Maschine",
      de_CH: "Maschine",
      en: "Machine",
      en_GB: "Machine",
      es: "Máquina",
      fr: "Machine",
      hu: "Gép",
      it: "Macchina",
      pl: "Maszyna",
      pt: "Máquina",
      uk_UA: "Машина",
    },
  ],
  [
    "METHOD",
    {
      de: "Methode",
      de_CH: "Methode",
      en: "Method",
      en_GB: "Method",
      es: "Método",
      fr: "Méthode",
      hu: "Módszer",
      it: "Metodo",
      pl: "Metoda",
      pt: "Método",
      uk_UA: "Метод",
    },
  ],
  [
    "MATERIAL",
    {
      de: "Material",
      de_CH: "Material",
      en: "Material",
      en_GB: "Material",
      es: "Material",
      fr: "Matière",
      hu: "Anyag",
      it: "Materiale",
      pl: "Materiał",
      pt: "Material",
      uk_UA: "Матеріал",
    },
  ],
  [
    "ENVIRONMENT",
    {
      de: "Mitwelt",
      de_CH: "Mitwelt",
      en: "Environment",
      en_GB: "Environment",
      es: "Entorno",
      fr: "Environnement",
      hu: "Környezet",
      it: "Ambiente",
      pl: "Środowisko",
      pt: "Ambiente",
      uk_UA: "Середовище",
    },
  ],
] as const;

export function createDefaultIshikawaConfig(
  _translate: (key: string) => unknown,
): IshikawaFieldConfig {
  return {
    areas: DEFAULT_AREA_DEFINITIONS.map(([id, translations]) => ({
      id,
      translations: { ...translations },
    })),
    problemSource: null,
  };
}

export function normalizeIshikawaConfig(
  value: unknown,
  translate: (key: string) => unknown,
): IshikawaFieldConfig {
  if (value == null || typeof value !== "object" || Array.isArray(value)) {
    return createDefaultIshikawaConfig(translate);
  }

  const config = value as Record<string, unknown>;
  const areas = Array.isArray(config.areas)
    ? config.areas.flatMap((entry): IshikawaAreaConfig[] => {
        if (entry == null || typeof entry !== "object" || Array.isArray(entry)) return [];
        const area = entry as Record<string, unknown>;
        if (typeof area.id !== "string") return [];
        const legacyLabel = typeof area.label === "string" ? area.label : "";
        const translations =
          area.translations != null &&
          typeof area.translations === "object" &&
          !Array.isArray(area.translations)
            ? Object.fromEntries(
                Object.entries(area.translations).filter(
                  ([key, label]) =>
                    Object.prototype.hasOwnProperty.call(languageNames, key) &&
                    typeof label === "string",
                ),
              )
            : legacyLabel
              ? { de: legacyLabel }
              : {};
        return [
          {
            id: area.id,
            ...(typeof area.nameId === "number" ? { nameId: area.nameId } : {}),
            translations,
          },
        ];
      })
    : [];
  const source = config.problemSource;
  const problemSource =
    source != null && typeof source === "object" && !Array.isArray(source)
      ? (source as Record<string, unknown>)
      : undefined;

  return {
    areas:
      areas.length > 0
        ? areas.slice(0, MAX_ISHIKAWA_AREAS)
        : createDefaultIshikawaConfig(translate).areas,
    problemSource:
      Number.isInteger(problemSource?.rowIndex) && Number.isInteger(problemSource?.columnIndex)
        ? {
            rowIndex: Number(problemSource?.rowIndex),
            columnIndex: Number(problemSource?.columnIndex),
          }
        : null,
  };
}

export type IshikawaSourceField = {
  type: SQDCEventActionTypeFieldType;
  rowIndex: number;
  columnIndex: number;
};

export function isConfiguredProblemSource(
  field: IshikawaSourceField,
  source: IshikawaProblemSource | null,
) {
  return field.rowIndex === source?.rowIndex && field.columnIndex === source.columnIndex;
}

export function localizedIshikawaAreaLabel(area: IshikawaAreaConfig, locale: string): string {
  const translations = area.translations;
  const key = locale.replaceAll("-", "_") as keyof ILanguage;
  const baseKey = locale.split(/[-_]/)[0] as keyof ILanguage;
  return (translations?.[key] ||
    translations?.[baseKey] ||
    translations?.en ||
    translations?.en_GB ||
    translations?.de ||
    "") as string;
}

export function localizedIshikawaAreas(
  areas: IshikawaAreaConfig[],
  locale: string,
): { id: string; label: string }[] {
  return areas.map((area) => ({ id: area.id, label: localizedIshikawaAreaLabel(area, locale) }));
}
